import { Controller } from '@hotwired/stimulus'
import { showFlash } from '../utils/flash_messages'

/**
 * CartNameEditController
 * 
 * Handles inline editing of cart names with toggle between display and edit modes.
 * Uses native fetch API for form submissions to properly handle JSON responses.
 */
export default class extends Controller {
  static targets = ['display', 'edit', 'input', 'nameLink', 'form']

  connect() {
    // Find the form in the edit target and attach submit handler
    const form = this.editTarget?.querySelector('form')
    if (form) {
      form.addEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  disconnect() {
    const form = this.editTarget?.querySelector('form')
    if (form) {
      form.removeEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  /**
   * Show the edit form and hide the display view
   */
  showEdit() {
    this.displayTarget.classList.add('d-none')
    this.editTarget.classList.remove('d-none')
    
    // Focus the input field
    if (this.hasInputTarget) {
      this.inputTarget.focus()
      this.inputTarget.select()
    }
  }

  /**
   * Cancel editing and return to display view
   */
  cancelEdit() {
    this.editTarget.classList.add('d-none')
    this.displayTarget.classList.remove('d-none')
  }

  /**
   * Handle form submission via fetch API
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    event.preventDefault()
    
    const form = event.target
    const formData = new FormData(form)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const submitButton = form.querySelector('button[type="submit"]')

    if (submitButton) {
      submitButton.disabled = true
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData,
        credentials: 'same-origin'
      })

      const contentType = response.headers.get('content-type')
      const isJson = contentType && contentType.includes('application/json')

      if (!response.ok) {
        await this.handleError(response, isJson)
        return
      }

      if (isJson) {
        const data = await response.json()
        this.handleSuccess(data)
      }
    } catch (error) {
      console.error('Cart name edit error:', error)
      showFlash({ type: 'danger', message: 'An unexpected error occurred. Please try again.' })
    } finally {
      if (submitButton) {
        submitButton.disabled = false
      }
    }
  }

  /**
   * Handle successful save - update display with new name
   * @param {Object} data - Response data from server
   */
  handleSuccess(data) {
    // Update the displayed name if we have a nameLink target
    if (this.hasNameLinkTarget && this.hasInputTarget) {
      const newName = this.inputTarget.value.trim() || 'Unnamed Cart'
      this.nameLinkTarget.textContent = newName
      
      // Also update the cart header if it exists
      this.updateCartHeader(newName)
      
      // Update page title
      document.title = `Saved Cart: ${newName}`
    }
    
    // Show success message if provided
    if (data.message) {
      showFlash({ type: 'success', message: data.message })
    }
    
    // Return to display mode
    this.cancelEdit()
  }

  /**
   * Update the cart header with the new name
   * @param {string} newName - The new cart name
   */
  updateCartHeader(newName) {
    const cartHeaderName = document.getElementById('cart-name')
    if (cartHeaderName) {
      cartHeaderName.textContent = newName
    }
  }

  /**
   * Handle error - stay in edit mode so user can try again
   * @param {Response} response - The fetch response
   * @param {boolean} isJson - Whether the response is JSON
   */
  async handleError(response, isJson) {
    let errorMessage = 'An error occurred while saving the cart name'
    
    if (isJson) {
      try {
        const data = await response.json()
        if (data.error) {
          errorMessage = data.error
        }
      } catch (e) {
        // Use default error message
      }
    }
    
    showFlash({ type: 'danger', message: errorMessage })
    // Keep edit mode open so user can correct and retry
  }
}
